/**
 * KioskMode — global behaviour module.
 *
 * Activation (either path enables it):
 *   1. Backend config `enabled=true` (admin toggle, persisted in Mongo).
 *   2. URL query string contains `?kiosk=1` (volatile, for ad-hoc tablets).
 *
 * While active:
 *   - Resets the experience to `/menu` (and clears language + selection)
 *     after N idle minutes of no user input.
 *
 * Exit:
 *   - PIN modal (5 quick taps on the PONT10 logo opens it).
 *   - Correct PIN 091764 disables kiosk (turns off backend flag AND strips
 *     the ?kiosk=1 query param from the URL).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSelection } from '../context/SelectionContext';
import AttractScreen from './AttractScreen';

const API = process.env.REACT_APP_BACKEND_URL || '';
const KIOSK_PIN = '091764';
// Per-device, session-scoped opt-out: set by the PIN-exit flow so the
// current tablet can temporarily leave kiosk mode without touching the
// global backend flag. Cleared on page reload — a fresh boot re-engages
// kiosk if the admin still has it on.
const DEVICE_EXIT_KEY = 'pont10_kiosk_device_exit';
const hasDeviceExit = () => {
  try { return sessionStorage.getItem(DEVICE_EXIT_KEY) === '1'; }
  catch { return false; }
};

const isKioskUrl = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('kiosk') === '1';
  } catch { return false; }
};

export const KioskMode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearLanguage } = useLanguage();
  const { clearSelection } = useSelection();

  const [serverEnabled, setServerEnabled] = useState(false);
  const [idleMinutes, setIdleMinutes] = useState(5);
  const [showPin, setShowPin] = useState(false);
  const [showAttract, setShowAttract] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [deviceExit, setDeviceExit] = useState(hasDeviceExit);
  const [pinError, setPinError] = useState('');
  const tapCountRef = useRef(0);
  const tapResetRef = useRef(null);
  const idleTimerRef = useRef(null);
  const attractTimerRef = useRef(null);

  // Fetch kiosk config once at mount (refreshes on full reload).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/kiosk/config`);
        if (!r.ok) return;
        const cfg = await r.json();
        if (cancelled) return;
        setServerEnabled(!!cfg.enabled);
        setIdleMinutes(Math.max(1, Math.min(120, Number(cfg.idle_minutes) || 5)));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const isActive = (serverEnabled || isKioskUrl()) && !deviceExit;

  // Reset back to /menu, clearing transient state. Skip when already on
  // /menu so the auto-reset doesn't fight the user navigating manually.
  const resetExperience = useCallback(async () => {
    try { await clearSelection?.(); } catch { /* silent */ }
    try { clearLanguage?.(); } catch { /* silent */ }
    try { sessionStorage.removeItem('pont10_entry_choice'); } catch { /* silent */ }
    try { sessionStorage.removeItem('pont10_experience'); } catch { /* silent */ }
    navigate('/menu', { replace: true });
  }, [navigate, clearSelection, clearLanguage]);

  // Idle timers:
  //   - Attract screen surfaces at 50% of the idle window.
  //   - Full idle window → reset experience to /menu.
  // Any user input dismisses the attract overlay AND re-arms both timers.
  useEffect(() => {
    if (!isActive) return;
    const fullMs = idleMinutes * 60 * 1000;
    const attractMs = Math.max(15_000, Math.floor(fullMs / 2));
    const arm = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (attractTimerRef.current) clearTimeout(attractTimerRef.current);
      setShowAttract(false);
      attractTimerRef.current = setTimeout(() => setShowAttract(true), attractMs);
      idleTimerRef.current = setTimeout(() => {
        setShowAttract(false);
        resetExperience();
      }, fullMs);
    };
    const events = ['pointerdown', 'touchstart', 'mousedown', 'keydown', 'wheel'];
    events.forEach(e => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => {
      events.forEach(e => window.removeEventListener(e, arm));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (attractTimerRef.current) clearTimeout(attractTimerRef.current);
    };
  }, [isActive, idleMinutes, resetExperience]);

  // Five quick taps on the PONT10 logo open the PIN modal to exit kiosk.
  useEffect(() => {
    if (!isActive) return;
    const onTap = (ev) => {
      const t = ev.target;
      if (!t || !t.closest) return;
      // Match any element marked as the brand logo.
      const isLogo =
        t.closest('[data-testid="topbar-pont10-logo"]') ||
        t.closest('[data-testid="pont10-title"]');
      if (!isLogo) return;
      tapCountRef.current += 1;
      if (tapResetRef.current) clearTimeout(tapResetRef.current);
      tapResetRef.current = setTimeout(() => { tapCountRef.current = 0; }, 2000);
      if (tapCountRef.current >= 5) {
        tapCountRef.current = 0;
        setShowPin(true);
        setPinInput('');
        setPinError('');
      }
    };
    window.addEventListener('pointerdown', onTap);
    return () => window.removeEventListener('pointerdown', onTap);
  }, [isActive]);

  const handleSubmitPin = () => {
    if (pinInput !== KIOSK_PIN) {
      setPinError('PIN incorrecto');
      return;
    }
    // Per-device opt-out (works on any tablet, no admin token required).
    try { sessionStorage.setItem(DEVICE_EXIT_KEY, '1'); } catch { /* silent */ }
    setDeviceExit(true);
    // Also strip ?kiosk=1 if it was set via URL so the user lands cleanly.
    if (isKioskUrl()) {
      const sp = new URLSearchParams(window.location.search);
      sp.delete('kiosk');
      const qs = sp.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
    }
    // Force a state recompute so isActive turns false right away.
    setShowPin(false);
  };

  if (!isActive) return null;

  return (
    <>
      <AttractScreen
        open={showAttract && !showPin}
        onDismiss={() => setShowAttract(false)}
      />
      {showPin && (
        <div
          data-testid="kiosk-pin-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 99998,
            background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setShowPin(false)}
        >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1F1D1B', border: '1px solid rgba(200,155,60,0.3)',
          borderRadius: 22, padding: '32px 28px',
          maxWidth: 380, width: '100%', textAlign: 'center',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <p style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 22, color: '#F9F6F0', margin: '0 0 8px',
        }}>
          Salir del modo Kiosko
        </p>
        <p style={{ fontSize: 14, color: '#A39F98', margin: '0 0 20px' }}>
          Introduce el PIN para desbloquear.
        </p>
        <input
          data-testid="kiosk-pin-input"
          type="password"
          inputMode="numeric"
          autoFocus
          value={pinInput}
          onChange={(e) => { setPinInput(e.target.value); setPinError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitPin(); }}
          placeholder="PIN"
          style={{
            width: '100%', padding: '14px 16px', fontSize: 22,
            textAlign: 'center', letterSpacing: '0.5em',
            background: '#161514', color: '#F9F6F0',
            border: '1px solid #33302E', borderRadius: 12,
            outline: 'none', marginBottom: 12,
          }}
        />
        {pinError && (
          <p data-testid="kiosk-pin-error" style={{ color: '#E07A5F', fontSize: 13, margin: '0 0 12px' }}>
            {pinError}
          </p>
        )}
        <button
          data-testid="kiosk-pin-submit"
          onClick={handleSubmitPin}
          style={{
            width: '100%', padding: 14,
            border: 'none', borderRadius: 999,
            background: 'linear-gradient(135deg, #C89B3C, #D4AF37)',
            color: '#1A1A1A', fontWeight: 600, fontSize: 15, cursor: 'pointer',
            marginBottom: 8,
          }}
        >
          Desbloquear
        </button>
        <button
          data-testid="kiosk-pin-cancel"
          onClick={() => setShowPin(false)}
          style={{
            width: '100%', padding: 12, border: 'none', borderRadius: 999,
            background: 'transparent', color: '#A39F98', fontSize: 14, cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
      </div>
        </div>
      )}
    </>
  );
};

export default KioskMode;
